import type { NotificationEventType, NotificationSubscription } from '@gopinhos/shared';
import { Bell, Check } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  useNotificationSubscriptions,
  useUpdateNotificationSubscriptions,
} from '../hooks/use-notification-subscriptions';
import { useProjects } from '../hooks/use-projects';

const EVENT_TYPES: NotificationEventType[] = [
  'lead.created',
  'lead.moved',
  'lead.assigned',
  'lead.commented',
  'lead.documented',
  'reminder.due',
  'project.notes_updated',
];

function eventLabel(t: (k: string) => string, e: NotificationEventType): string {
  return t(`notifSettings.events.${e}`);
}

export function NotificationSettingsPage(): JSX.Element {
  const { t } = useTranslation();
  const subs = useNotificationSubscriptions();
  const projects = useProjects();
  const update = useUpdateNotificationSubscriptions();

  // Local state mirroring the server's stored opt-OUTs. A missing entry
  // means default-on; an entry with enabled=false means opted out.
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!subs.data) return;
    const map: Record<string, boolean> = {};
    for (const s of subs.data.items) {
      map[`${s.scope}|${s.scopeId ?? ''}|${s.eventType}`] = s.enabled;
    }
    setOverrides(map);
  }, [subs.data]);

  const projectsList = useMemo(
    () => (projects.data?.items ?? []).filter((p) => !p.archivedAt),
    [projects.data],
  );

  if (subs.isLoading || projects.isLoading) return <Skeleton className="h-64" />;

  function isEnabled(scope: 'global' | 'project', scopeId: string | null, e: NotificationEventType): boolean {
    const k = `${scope}|${scopeId ?? ''}|${e}`;
    if (k in overrides) return overrides[k]!;
    return true; // default-on
  }

  function toggle(scope: 'global' | 'project', scopeId: string | null, e: NotificationEventType): void {
    const k = `${scope}|${scopeId ?? ''}|${e}`;
    setOverrides((cur) => ({ ...cur, [k]: !(k in cur ? cur[k] : true) }));
    setSavedAt(null);
  }

  async function save(): Promise<void> {
    const items: NotificationSubscription[] = Object.entries(overrides)
      .filter(([, enabled]) => enabled === false) // store only opt-OUTs
      .map(([key, enabled]) => {
        const [scope, scopeId, eventType] = key.split('|') as [
          'global' | 'project',
          string,
          NotificationEventType,
        ];
        return { scope, scopeId: scopeId || null, eventType, enabled };
      });
    await update.mutateAsync(items);
    setSavedAt(Date.now());
  }

  return (
    <div className="max-w-3xl space-y-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-brand-700">
            <Bell className="h-5 w-5" />
            <h1 className="text-2xl font-semibold">{t('notifSettings.title')}</h1>
          </div>
          <p className="mt-1 text-sm text-surface-600 max-w-2xl">{t('notifSettings.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {savedAt && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
              <Check className="h-3 w-3" /> {t('notifSettings.savedJustNow')}
            </span>
          )}
          <Button onClick={save} disabled={update.isPending}>
            {update.isPending ? t('notifSettings.saving') : t('notifSettings.save')}
          </Button>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('notifSettings.global')}</CardTitle>
          <p className="text-xs text-surface-500">{t('notifSettings.globalHint')}</p>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 sm:grid-cols-2">
            {EVENT_TYPES.map((e) => {
              const on = isEnabled('global', null, e);
              return (
                <label
                  key={e}
                  className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-surface-200 px-3 py-2 hover:border-brand-200"
                >
                  <span className="text-sm text-surface-800">{eventLabel(t, e)}</span>
                  <Toggle on={on} onChange={() => toggle('global', null, e)} />
                </label>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {projectsList.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('notifSettings.perProject')}</CardTitle>
            <p className="text-xs text-surface-500">{t('notifSettings.perProjectHint')}</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {projectsList.map((p) => (
                <div key={p.id} className="rounded-md border border-surface-200">
                  <header className="flex items-center gap-2 border-b border-surface-200 px-3 py-2">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
                    <span className="text-sm font-medium text-surface-800">{p.name}</span>
                  </header>
                  <div className="grid gap-2 p-3 sm:grid-cols-2">
                    {EVENT_TYPES.map((e) => {
                      const on = isEnabled('project', p.id, e);
                      return (
                        <label
                          key={e}
                          className="flex cursor-pointer items-center justify-between gap-3 rounded-md px-2 py-1.5 hover:bg-surface-50"
                        >
                          <span className="text-sm text-surface-700">{eventLabel(t, e)}</span>
                          <Toggle on={on} onChange={() => toggle('project', p.id, e)} />
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Toggle({ on, onChange }: { on: boolean; onChange: () => void }): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onChange}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${
        on ? 'bg-brand-600' : 'bg-surface-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${
          on ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}
